import type { ApiErrorPayload, ListParams, Paginated } from '@/types/api';

const API_URL: string = import.meta.env['VITE_API_URL'] ?? '/api/v1';
const TOKEN_STORAGE_KEY = 'wf.access_token';

/** Типизированная ошибка API: несёт HTTP-статус, машинный код и ошибки по полям. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly fieldErrors: Record<string, string[]>;

  constructor(status: number, payload: Partial<ApiErrorPayload>) {
    super(payload.message ?? 'Не удалось выполнить запрос');
    this.name = 'ApiError';
    this.status = status;
    this.code = payload.code ?? 'unknown_error';
    this.details = payload.details;
    this.fieldErrors = payload.fieldErrors ?? {};
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Конфликт версий или бизнес-правило: показываем текст пользователю как есть. */
  get isConflict(): boolean {
    return this.status === 409 || this.status === 422;
  }
}

let accessToken: string | null = readStoredToken();
let onUnauthorized: (() => void) | null = null;

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  try {
    if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* приватный режим браузера — работаем только в памяти */
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Регистрирует реакцию на 401: обычно — разлогин и переход на /login. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/** Сериализация query-параметров: массивы разворачиваются в повторяющиеся ключи. */
export function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === '') continue;
        search.append(key, String(item));
      }
    } else if (value instanceof Date) {
      search.append(key, value.toISOString().slice(0, 10));
    } else {
      search.append(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  query?: Record<string, unknown>;
  body?: unknown;
  /** Не подставлять заголовок авторизации (логин, восстановление пароля). */
  anonymous?: boolean;
  /** Ожидаемый тип ответа: json по умолчанию. */
  responseType?: 'json' | 'blob' | 'text' | 'void';
}

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const { query, body, anonymous, responseType = 'json', headers, ...rest } = options;
  const url = `${API_URL}${path}${buildQuery(query)}`;

  const finalHeaders = new Headers(headers);
  finalHeaders.set('Accept', 'application/json');
  if (!anonymous && accessToken) finalHeaders.set('Authorization', `Bearer ${accessToken}`);

  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    finalHeaders.set('Content-Type', 'application/json');
    payload = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(url, { method, headers: finalHeaders, body: payload, ...rest });
  } catch (cause) {
    // Сеть недоступна или запрос прерван — отдаём ошибку в том же формате.
    throw new ApiError(0, {
      code: 'network_error',
      message: 'Сервер недоступен. Проверьте соединение с сетью.',
      details: cause,
    });
  }

  if (response.status === 401 && !anonymous) {
    setAccessToken(null);
    onUnauthorized?.();
  }

  if (!response.ok) {
    let parsed: Partial<ApiErrorPayload> = {};
    try {
      parsed = (await response.json()) as Partial<ApiErrorPayload>;
    } catch {
      parsed = { message: response.statusText };
    }
    throw new ApiError(response.status, parsed);
  }

  if (responseType === 'void' || response.status === 204) return undefined as T;
  if (responseType === 'blob') return (await response.blob()) as T;
  if (responseType === 'text') return (await response.text()) as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
  /** Списочный запрос с постраничной разбивкой. */
  list: <T>(path: string, params?: ListParams) =>
    request<Paginated<T>>('GET', path, { query: params as Record<string, unknown> }),
  /** Скачивание файла (печатные формы, выгрузки). */
  download: async (path: string, params?: Record<string, unknown>, filename?: string) => {
    const blob = await request<Blob>('GET', path, { query: params, responseType: 'blob' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = filename ?? path.split('/').pop() ?? 'file';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(href);
  },
};

/** Человекочитаемое сообщение об ошибке для тостов. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Неизвестная ошибка';
}
